import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { getSavedMicrosoftProviderNames } from '../DataProvider/DataProvider.utils';
import { selectedSourceDetailsAtom } from '../SourceCreate.store';
import {
  type SourceCreateDynamicFormModel,
  SourceDialogMode
} from '../SourceCreate.types';

/**
 * Microsoft data providers that support site name inheritance.
 *
 * Site names help organize log sources within the platform's hierarchy.
 * When setting up multiple Microsoft providers at once, inheriting the
 * root site name ensures consistency across all sub-providers.
 */
const MICROSOFT_PROVIDERS_WITH_SITE_NAME = [
  'data_provider.MicrosoftEntraID.GraphAPI',
  'data_provider.MicrosoftEntraID.O365API',
  'data_provider.O365',
  'data_provider.MicrosoftDefenderUnified'
] as const;

const MICROSOFT_PROVIDER_SELECTION_PATHS = [
  'data_provider.MicrosoftEntraID.GraphAPI.selected',
  'data_provider.MicrosoftEntraID.O365API.selected',
  'data_provider.O365.selected',
  'data_provider.MicrosoftDefenderUnified.selected'
] as const;

const MICROSOFT_PROVIDER_SITE_NAME_PATHS = [
  'data_provider.MicrosoftEntraID.GraphAPI.site_name',
  'data_provider.MicrosoftEntraID.O365API.site_name',
  'data_provider.O365.site_name',
  'data_provider.MicrosoftDefenderUnified.site_name'
] as const;

/**
 * Extracts the provider key from a full provider path.
 *
 * @example
 * getProviderKey('data_provider.MicrosoftEntraID.GraphAPI') // => 'GraphAPI'
 * getProviderKey('data_provider.O365') // => 'O365'
 */
const getProviderKey = (providerPath: string): string =>
  providerPath.split('.').pop() || '';

/**
 * Automatically inherits site name from root form to Microsoft data providers.
 *
 * BUSINESS RULES:
 * 1. **New Mode**: All selected providers inherit the root site name by default
 * 2. **Edit Mode**: Only newly added providers inherit; existing providers keep their current site names
 * 3. **User Override**: If user manually edits a provider's site name, stop auto-inheritance for that provider
 *
 * WHY THIS EXISTS:
 * - Consistency: Ensures all Microsoft log sources use the same site name
 * - UX: Saves users from manually entering the same site name for each provider
 * - Safety: Preserves user edits and doesn't overwrite existing saved configurations
 *
 * @param mode - 'New' or 'Edit' - determines inheritance behavior for existing providers
 */
const useSiteNameInheritance = (mode: string) => {
  const { setValue, control } = useFormContext<SourceCreateDynamicFormModel>();
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const inheritedSiteNamesRef = useRef<Record<string, string>>({});
  const userEditedSiteNamesRef = useRef<Record<string, boolean>>({});
  const previousSiteNamesRef = useRef<Record<string, string>>({});

  // Root site name that should cascade to all providers
  const rootSiteName = useWatch({ control, name: 'site_name' });

  // Track which providers are currently selected
  const selectedProviders = useWatch({
    control,
    name: MICROSOFT_PROVIDER_SELECTION_PATHS
  });

  const providerSiteNames = useWatch({
    control,
    name: MICROSOFT_PROVIDER_SITE_NAME_PATHS
  });

  useEffect(() => {
    // Wait for root site name and provider selection state to be available
    if (!rootSiteName || !selectedProviders || !providerSiteNames) return;

    // In Edit mode, get list of providers that already exist in the database
    const existingProviderKeys = getSavedMicrosoftProviderNames(
      selectedSourceDetails
    );

    MICROSOFT_PROVIDERS_WITH_SITE_NAME.forEach((providerPath, index) => {
      const isProviderSelected = selectedProviders[index];

      // Only process selected providers
      if (!isProviderSelected) return;

      const providerSiteNamePath = `${providerPath}.site_name` as const;
      const currentSiteName = String(providerSiteNames[index] ?? '');
      const lastInheritedSiteName = inheritedSiteNamesRef.current[providerPath];
      const previousSiteName = previousSiteNamesRef.current[providerPath];

      // Track explicit user edits separately from inherited or uninitialized values.
      // Empty string is a valid manual override for this optional suffix.
      if (
        previousSiteName !== undefined &&
        previousSiteName !== currentSiteName &&
        currentSiteName !== rootSiteName &&
        currentSiteName !== lastInheritedSiteName
      ) {
        userEditedSiteNamesRef.current[providerPath] = true;
      }
      previousSiteNamesRef.current[providerPath] = currentSiteName;

      const providerKey = getProviderKey(providerPath);
      const providerExistsInDatabase =
        existingProviderKeys.includes(providerKey);

      // Rule 2: In Edit mode, preserve existing provider's site name
      // Only inherit for newly added providers
      if (mode === SourceDialogMode.Edit && providerExistsInDatabase) return;

      const hasExplicitUserOverride =
        userEditedSiteNamesRef.current[providerPath] ?? false;
      const isUsingInheritedValue =
        !hasExplicitUserOverride &&
        (currentSiteName === '' ||
          currentSiteName === rootSiteName ||
          currentSiteName === lastInheritedSiteName);

      // Rule 3: Never override user's manual edits
      if (!isUsingInheritedValue) return;

      // Rule 1: Inherit root site name for new providers (or all providers in New mode)
      if (currentSiteName !== rootSiteName) {
        setValue(
          providerSiteNamePath as keyof SourceCreateDynamicFormModel,
          rootSiteName,
          {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false
          }
        );
      }

      inheritedSiteNamesRef.current[providerPath] = rootSiteName;
      userEditedSiteNamesRef.current[providerPath] = false;
    });
  }, [
    rootSiteName,
    selectedProviders,
    providerSiteNames,
    mode,
    selectedSourceDetails,
    setValue
  ]);
};

export default useSiteNameInheritance;
